import type { ReactNode } from 'react';

import { DesktopSearchBar } from '#/components/DesktopSearchBar/DesktopSearchBar';
import { DesktopSearchBarLocationInput } from '#/components/DesktopSearchBar/DesktopSearchBarLocationInput';
import { DesktopSearchBarQueryInput } from '#/components/DesktopSearchBar/DesktopSearchBarQueryInput';
import { DesktopSearchBarSearchTypeSelect } from '#/components/DesktopSearchBar/DesktopSearchBarSearchTypeSelect';
import { DesktopSearchBarSubmitButton } from '#/components/DesktopSearchBar/DesktopSearchBarSubmitButton';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { Icon } from '#/components/Icon/Icon';
import {
  LocationRadiusSelect,
  type LocationRadiusSelectHandle,
} from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { MobileSearchMenu } from '#/components/MobileSearchMenu/MobileSearchMenu';
import { PageHeaderButton } from '#/components/PageHeader/Button/PageHeaderButton';
import { PageHeader } from '#/components/PageHeader/PageHeader';
import { CartDropdownButtonContainerLoadable } from '#/containers/CartDropdownButton/CartDropdownButtonContainerLoadable';
import { FlashMessageContainer } from '#/containers/FlashMessageContainer';
import { MyAccountDropdownContainerLoadable } from '#/containers/MyAccountDropdown/MyAccountDropdownContainerLoadable';
import { NotificationsDropdownContainerLoadable } from '#/containers/NotificationsDropdown/NotificationsDropdownContainerLoadable';
import { useEnvironment, useUser } from '#/contexts/UserEnvironmentContext';
import { useLogin } from '#/hooks/useLogin';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useSearchRadius } from '#/modules/search/contexts/SearchContext';
import { isPointGeotype } from '#/modules/search/helpers/isPointGeotype';
import { useSearchRadiusOptions } from '#/modules/search/hooks/facets/useSearchRadiusOptions';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import type { SearchFromPageType } from '#/modules/search/types/SearchFromPageType';
import type { SearchType } from '#/modules/search/types/SearchType';
import { getDonateRoutePath } from '#/routing/utils/getDonateRoutePath';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { showModal } from '#/zustand-stores/modalStore';

import { pageHeaderContainerDonateTextByLocale } from './pageHeaderContainerDonateTextByLocale';
import { usePageHeaderContainerSearch } from './usePageHeaderContainerSearch';

type Props = {
  searchRefinements?: ReactNode;
  showDesktopSearch: boolean;
  defaultSearchType: SearchType;
  progressBar: ReactNode;
  isInSearchPage: boolean;
  isInSearchLandingPage: boolean;
  searchFromPageType?: SearchFromPageType;
};

export function PageHeaderContainer({
  searchRefinements,
  showDesktopSearch,
  defaultSearchType,
  progressBar,
  isInSearchPage,
  isInSearchLandingPage,
  searchFromPageType,
}: Props) {
  const {
    initialQuery,
    searchLocation,
    changeSearchLocation,
    search,
    searchOnClear,
  } = usePageHeaderContainerSearch({
    defaultSearchType,
    isInSearchPage,
    isInSearchLandingPage,
    embedSearchWidgetId: null,
    searchFromPageType,
  });

  const { user } = useUser();
  const { environment } = useEnvironment();
  const { i18n, locale } = useI18n();

  const { loginLink, onClickLogin } = useLogin();

  const radius = useSearchRadius();
  const { setSearchUrlRadius } = useSearchPageActions({
    isInSearchLandingPage,
  });

  const radiusOptions = useSearchRadiusOptions();

  const radiusHandle: LocationRadiusSelectHandle = isInSearchPage
    ? {
        type: 'controlled',
        onChange: setSearchUrlRadius,
        radius,
      }
    : { type: 'uncontrolled', radius };

  return (
    <PageHeader
      loginHref={loginLink}
      signupHref="/signup"
      isUserLoaded={Boolean(environment)}
      onClickLogin={onClickLogin}
      onClickSignup={(e) => {
        e.preventDefault();
        showModal('SIGNUP', {});
      }}
      isInSearchPage={isInSearchPage}
      donate={{
        href: getDonateRoutePath(locale),
        text: pageHeaderContainerDonateTextByLocale[locale],
      }}
      userDisplayName={user?.name ?? user?.email}
      orgs={user?.orgs ?? []}
      notificationsDropdown={<NotificationsDropdownContainerLoadable />}
      myAccountDropdown={<MyAccountDropdownContainerLoadable />}
      cartDropdownButton={<CartDropdownButtonContainerLoadable />}
      desktopSearchBar={
        showDesktopSearch && (
          <DesktopSearchBar
            defaultSearchType={defaultSearchType}
            onSearch={search}
            searchTypeSelect={<DesktopSearchBarSearchTypeSelect />}
            locationInput={
              <DesktopSearchBarLocationInput
                searchLocation={searchLocation ?? null}
                onChangeSearchLocation={changeSearchLocation}
              />
            }
            radiusInput={
              <LocationRadiusSelect
                handle={radiusHandle}
                options={radiusOptions}
                disabled={
                  !searchLocation || !isPointGeotype(searchLocation.geoType)
                }
                qaId="page-header-desktop-location-radius"
              />
            }
            queryInput={
              <DesktopSearchBarQueryInput
                initialQuery={initialQuery}
                searchOnClear={searchOnClear}
              />
            }
            submitButton={<DesktopSearchBarSubmitButton />}
          />
        )
      }
      mobileSearchDropdown={
        <Dropdown
          trigger={
            <PageHeaderButton
              type="inert"
              ariaLabel={i18n('Toggle search')}
              qaId="search-menu-button"
              variant="grey"
            >
              <Icon name="search" size={24} />
            </PageHeaderButton>
          }
          onToggle={(open) =>
            open && trackClicked('Header Utility CTA - Search')
          }
        >
          <MobileSearchMenu
            defaultSearchType={defaultSearchType}
            initialQuery={initialQuery}
            searchLocation={searchLocation}
            onChangeSearchLocation={changeSearchLocation}
            radiusOptions={radiusOptions}
            radiusHandle={radiusHandle}
            onSearch={search}
            searchOnClear={searchOnClear}
          />
        </Dropdown>
      }
      below={
        <>
          {searchRefinements}
          <FlashMessageContainer />
          {progressBar}
        </>
      }
    />
  );
}
